import { Box, Flex, Image, Text, useColorModeValue } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import type { SocketMessage } from 'lib/socket/types';
import type { Block } from 'types/api/block';

import { route } from 'nextjs-routes';

import useApiQuery, { getResourceKey } from 'lib/api/useApiQuery';
import { useI18n } from 'lib/i18n/context';
import useSocketChannel from 'lib/socket/useSocketChannel';
import useSocketMessage from 'lib/socket/useSocketMessage';
import { BLOCK } from 'stubs/block';
import { HOMEPAGE_STATS } from 'stubs/stats';
import Skeleton from 'ui/shared/chakra/Skeleton';
import LinkInternal from 'ui/shared/links/LinkInternal';

import LatestBlocksItem from './LatestBlocksItem';

const ACCENT_COLOR = '#14DCAC';

const LatestBlocks = () => {
  const cardBg = useColorModeValue('#FFFFFF', '#111111');
  const borderColor = useColorModeValue('#E9ECEF', '#222222');
  const titleColor = useColorModeValue('#616161', '#999999');
  const valueColor = useColorModeValue('#000000', '#FFFFFF');
  const subLabelColor = useColorModeValue('#999999', '#616161');
  const arrowSrc = useColorModeValue('/static/homepage/latest-blocks-arrow-light.svg', '/static/homepage/latest-blocks-arrow.svg');
  const { t } = useI18n();
  const blocksMaxCount = 5;
  const { data, isPlaceholderData, isError } = useApiQuery('homepage_blocks', {
    queryOptions: {
      placeholderData: Array(blocksMaxCount).fill(BLOCK),
    },
  });

  const queryClient = useQueryClient();
  const statsQueryResult = useApiQuery('stats', {
    queryOptions: {
      refetchOnMount: false,
      placeholderData: HOMEPAGE_STATS,
    },
  });

  const handleNewBlockMessage: SocketMessage.NewBlock['handler'] = React.useCallback((payload) => {
    queryClient.setQueryData(getResourceKey('homepage_blocks'), (prevData: Array<Block> | undefined) => {
      const newData = prevData ? [ ...prevData ] : [];

      if (newData.some((block) => block.height === payload.block.height)) {
        return newData;
      }

      return [ payload.block, ...newData ].sort((b1, b2) => b2.height - b1.height).slice(0, blocksMaxCount);
    });
  }, [ blocksMaxCount, queryClient ]);

  const channel = useSocketChannel({
    topic: 'blocks:new_block',
    isDisabled: isPlaceholderData || isError,
  });

  useSocketMessage({
    channel,
    event: 'new_block',
    handler: handleNewBlockMessage,
  });

  const blocksUrl = route({ pathname: '/blocks' });

  return (
    <Box
      width={{ base: '100%', lg: '480px' }}
      bg={ cardBg }
      border="1px solid"
      borderColor={ borderColor }
      borderRadius="8px"
      overflow="hidden"
      flexShrink={ 0 }
    >
      <Box px="16px" py="16px" minH="74px" borderBottom="1px solid" borderColor={ borderColor }>
        <Flex justifyContent="space-between" alignItems="center">
          <Text
            color={ titleColor }
            fontSize="16px"
            lineHeight="20.42px"
            fontWeight={ 500 }
            fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
          >
            { t('home.latestBlocks') }
          </Text>
          <LinkInternal
            href={ blocksUrl }
            display="inline-flex"
            alignItems="center"
            gap="4px"
            color={ valueColor }
            fontSize="14px"
            lineHeight="24px"
            fontWeight={ 500 }
            fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
            _hover={{ color: valueColor, textDecoration: 'none' }}
          >
            <span>{ t('home.viewAllBlocks') }</span>
            <Image src={ arrowSrc } alt="" boxSize="16px" flexShrink={ 0 }/>
          </LinkInternal>
        </Flex>
        { statsQueryResult.data?.network_utilization_percentage !== undefined ? (
          <Skeleton isLoaded={ !statsQueryResult.isPlaceholderData } display="inline-block" mt="12px">
            <Flex alignItems="baseline" gap="8px">
              <Text
                color={ subLabelColor }
                fontSize="14px"
                lineHeight="17.86px"
                fontWeight={ 500 }
                fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
              >
                { t('common.networkUtilization') }:
              </Text>
              <Text
                color={ ACCENT_COLOR }
                fontSize="14px"
                lineHeight="17.5px"
                fontWeight={ 500 }
                fontFamily="'Oxanium', var(--chakra-fonts-body)"
              >
                { statsQueryResult.data.network_utilization_percentage.toFixed(2) }%
              </Text>
            </Flex>
          </Skeleton>
        ) : null }
      </Box>

      { isError ? (
        <Box px="16px" py="24px">
          <Text color={ titleColor } fontSize="14px">{ t('home.noDataPleaseReloadThePage') }</Text>
        </Box>
      ) : null }

      { data ? (
        <AnimatePresence initial={ false }>
          { data.slice(0, blocksMaxCount).map((block, index, items) => (
            <LatestBlocksItem
              key={ block.height + (isPlaceholderData ? String(index) : '') }
              block={ block }
              isLoading={ isPlaceholderData }
              isLast={ index === items.length - 1 }
            />
          )) }
        </AnimatePresence>
      ) : null }
    </Box>
  );
};

export default LatestBlocks;
